import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Checkbox,
    IconButton,
    Typography,
    CircularProgress,
    Box,
    TextField,
    InputAdornment,
    FormControlLabel,
    Switch
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import callAPI from "../../Network/ApiConfig";
import Cookies from "universal-cookie";

const cookies = new Cookies();

// Helper function to get auth params from cookies
const getAuthParams = () => {
    return {
        username: cookies.get('user_name'),
        user_id: cookies.get('user_id'),
        account_name: cookies.get('account_name'),
        account_id: cookies.get('account_id'),
        sid: cookies.get('sid'),
        baseurl: cookies.get('baseUrl'),
        id: cookies.get('id'),
    }
}

export default function AssignAccountsDialog({ open, setOpen, contractor, onSave }) {
    const [t] = useTranslation("common");
    const maintheme = useSelector((state) => state.themeData.maintheme);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [selectedAccountIds, setSelectedAccountIds] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [showOnlySelected, setShowOnlySelected] = useState(false);

    // Filter accounts based on search
    const filteredAccounts = useMemo(() => {
        let result = accounts;

        if (showOnlySelected) {
            result = result.filter(acc => selectedAccountIds.includes(acc.account_id));
        }

        if (searchText) {
            const search = searchText.toLowerCase();
            result = result.filter(acc =>
                acc.account_name?.toLowerCase().includes(search)
            );
        }

        return result;
    }, [accounts, searchText, showOnlySelected, selectedAccountIds]);

    useEffect(() => {
        if (open && contractor) {
            loadAccounts();
        }
    }, [open, contractor]);

    const loadAccounts = async () => {
        setLoading(true);
        try {
            const result = await callAPI({
                route: "accounts",
                method: "get",
                params: {
                    ...getAuthParams(),
                    contractor_id: contractor.id
                },
                headers: { 'Content-Type': 'application/json' }
            });


            if (result?.status && result?.data?.data) {
                const accountsList = Array.isArray(result.data.data) ? result.data.data : (result.data.data.data || []);
                setAccounts(accountsList);
                // Pre-select accounts that already belong to this contractor
                const assignedIds = accountsList
                    .filter(acc => acc.contractor_id === contractor?.id)
                    .map(acc => acc.account_id);
                setSelectedAccountIds(assignedIds);
            }
        } catch (error) {
            console.log("Error loading accounts:", error);
            toast.error(t("NETWORK_MESSAGES.unknownError"));
        }
        setLoading(false);
    };

    const handleToggle = (accountId) => {
        setSelectedAccountIds((prev) => {
            if (prev.includes(accountId)) {
                return prev.filter((id) => id !== accountId);
            } else {
                return [...prev, accountId];
            }
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Update each account's contractor_id
            for (const account of accounts) {
                const shouldBeAssigned = selectedAccountIds.includes(account.account_id);
                const currentlyAssigned = account.contractor_id === contractor?.id;

                if (shouldBeAssigned && !currentlyAssigned) {
                    // Assign this contractor to this account
                    await callAPI({
                        route: `accounts/${account.account_id}/contractor`,
                        method: "put",
                        params: getAuthParams(),
                        data: { contractor_id: contractor.id },
                        headers: { "Content-Type": "application/x-www-form-urlencoded" }
                    });
                } else if (!shouldBeAssigned && currentlyAssigned) {
                    // Remove this contractor from this account
                    await callAPI({
                        route: `accounts/${account.account_id}/contractor`,
                        method: "put",
                        params: getAuthParams(),
                        data: { contractor_id: '' },
                        headers: { "Content-Type": "application/x-www-form-urlencoded" }
                    });
                }

            }

            toast.success(t("NETWORK_MESSAGES.updateSuccess") || "تم التحديث بنجاح");
            setOpen(false);
            onSave?.();
        } catch (error) {
            console.log("Error saving assignments:", error);
            toast.error(t("NETWORK_MESSAGES.unknownError"));
        }
        setSaving(false);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: "12px", minHeight: "400px" }
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    backgroundColor: maintheme?.primary,
                    color: "#fff",
                    fontFamily: "Cairo-Bold",
                }}
            >
                {t("GLOBAL.action_assign_accounts") || "تعيين الحسابات"} - {contractor?.name}
                <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ padding: "16px" }}>
                {/* Search Field */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder={t("GLOBAL.search") || "بحث"}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        sx={{
                            flex: 1,
                            '& .MuiInputBase-input': { fontFamily: 'Cairo' }
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showOnlySelected}
                                onChange={(e) => setShowOnlySelected(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Typography sx={{ fontFamily: 'Cairo-Medium', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                                {t("GLOBAL.show_selected_only") || "المختارة فقط"}
                            </Typography>
                        }
                    />
                </Box>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List sx={{ maxHeight: "350px", overflow: "auto" }}>
                        {filteredAccounts.length === 0 ? (
                            <Typography sx={{ textAlign: "center", fontFamily: "Cairo-Medium", color: "grey" }}>
                                {t("GLOBAL.no_data") || "لا توجد بيانات"}
                            </Typography>
                        ) : (
                            filteredAccounts.map((account) => (
                                <ListItem key={account.account_id} disablePadding>
                                    <ListItemButton onClick={() => handleToggle(account.account_id)} dense>
                                        <ListItemIcon>
                                            <Checkbox
                                                edge="start"
                                                checked={selectedAccountIds.includes(account.account_id)}
                                                tabIndex={-1}
                                                disableRipple
                                                sx={{
                                                    color: maintheme?.primary,
                                                    '&.Mui-checked': {
                                                        color: maintheme?.primary,
                                                    },
                                                }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={account.account_name}
                                            primaryTypographyProps={{ fontFamily: "Cairo-Medium" }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))
                        )}
                    </List>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: "16px" }}>
                <Button
                    onClick={handleClose}
                    sx={{ fontFamily: "Cairo-Medium" }}
                >
                    {t("GLOBAL.action_cancel") || "إلغاء"}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                    sx={{
                        fontFamily: "Cairo-Medium",
                        backgroundColor: maintheme?.primary,
                        "&:hover": { backgroundColor: maintheme?.secondary },
                    }}
                >
                    {saving ? <CircularProgress size={20} color="inherit" /> : (t("GLOBAL.action_save") || "حفظ")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
